// Single owner of all Kalshi REST I/O. Port of kalshi_client.py; transport now goes through the extension worker.

import { logOdd } from '../core/log';
import { countToMq, dollarsToMc, mcToDollarString, type MC, type MQ, dollarsToCash, type Cash } from '../core/money';
import {
  AmbiguousPlacement, type ClientOptions, type RawResponse, RestTransport, VenueAPIError as KalshiAPIError, sleep,
} from '../venue/rest';

// Re-exported because the engine imports these from here, even though the transport now lives in ../venue/rest.
export { AmbiguousPlacement, KalshiAPIError };
export type { ClientOptions, RawResponse };

export interface ShardBalance {
  exchangeIndex: number;
  balanceCash: Cash;
}

export interface ShardAllocation {
  exchangeIndex: number;
  percent: number;
}

/** Shard names per docs.kalshi.com/getting_started/exchange_sharding. */
export const SHARD_NAMES: Record<number, string> = {
  0: 'Default',
  1: 'Combos',
  2: 'Crypto & commodities',
  3: 'Sports',
};

export interface PlacePayload {
  ticker: string;
  side: 'yes' | 'no';
  countMq: MQ;
  yesPriceMc: MC;
  clientOrderId: string;
  mode: 'post' | 'fok';
  selfTradePreventionType?: string;
  /** Journal tag (arb order and leg). */
  tag?: string;
}

export interface NormalizedOrder {
  order_id: string | null;
  client_order_id: string | null;
  status: string;
  remaining_mq: MQ | null;
  fill_mq: MQ | null;
  yes_price_mc: MC | null;
  created_time?: string;
  queue_position?: number | null;
  raw: Record<string, unknown>;
  resolvedNote?: string;
}

export class KalshiClient extends RestTransport {
  private loggedQueueShape = false;

  constructor(opts: ClientOptions = {}) {
    super({ venue: 'kalshi', source: 'rest', read: [100, 200], cost: KalshiClient.requestCost, ...opts });
  }

  /** Documented non-default token costs; everything else is 10. */
  private static requestCost(method: string, path: string): number {
    const m = method.toUpperCase();
    if (m === 'DELETE' && path.includes('/orders/')) return 2;
    if (m === 'GET' && path.includes('/portfolio/orders/')) return 2; // single-order reads
    return 10;
  }

  /** Account balance, in Cash (1e-6 dollars). Kalshi returns integer cents. */
  async getBalance(): Promise<number> {
    const data = await this.getJson('/trade-api/v2/portfolio/balance');
    let bal = data.balance ?? 0;
    if (bal && typeof bal === 'object') bal = bal.balance ?? 0;
    return Math.round(Number(bal) * 10_000); // cents -> 1e-6 dollars
  }

  // Since exchange sharding (2026-08), collateral is held per shard — an order on a sports market draws on shard 3 only.
  async getBalanceBreakdown(): Promise<{ totalCash: Cash; shards: ShardBalance[] }> {
    const data = await this.getJson('/trade-api/v2/portfolio/balance');
    const rows: any[] = Array.isArray(data.balance_breakdown) ? data.balance_breakdown : [];
    const shards = rows
      .map((r) => ({ exchangeIndex: Number(r.exchange_index), balanceCash: dollarsToCash(r.balance) ?? 0 }))
      .filter((r) => Number.isFinite(r.exchangeIndex))
      .sort((a, b) => a.exchangeIndex - b.exchangeIndex);
    const totalCash = dollarsToCash(data.balance_dollars) ?? Math.round(Number(data.balance ?? 0) * 10_000);
    return { totalCash, shards };
  }

  // Asynchronous and non-atomic on Kalshi's side, so the balance breakdown catches up a few seconds later.
  async transferBetweenShards(fromShard: number, toShard: number, amountCash: Cash): Promise<string> {
    const centicents = Math.floor(amountCash / 100);
    if (centicents <= 0) throw new KalshiAPIError(400, 'transfer amount must be positive');
    const r = await this.request('POST', '/trade-api/v2/portfolio/intra_exchange_instance_transfer', {
      json: {
        source: 'event_contract', destination: 'event_contract', amount: centicents,
        source_exchange_shard: fromShard, destination_exchange_shard: toShard,
      },
    });
    if (r.status >= 300) throw new KalshiAPIError(r.status, r.text);
    const body = JSON.parse(r.text || '{}');
    return String(body.transfer_id ?? '');
  }

  /** Kalshi's own rebalancer: percentages per shard; empty means off. */
  async getTargetAllocation(): Promise<ShardAllocation[]> {
    const data = await this.getJson('/trade-api/v2/portfolio/target_balance_allocation');
    const rows: any[] = Array.isArray(data.allocations) ? data.allocations : [];
    return rows.map((r) => ({ exchangeIndex: Number(r.exchange_index), percent: Number(r.percent) }));
  }

  async setTargetAllocation(allocations: ShardAllocation[]): Promise<void> {
    const r = await this.request('POST', '/trade-api/v2/portfolio/target_balance_allocation', {
      json: { allocations: allocations.map((a) => ({ exchange_index: a.exchangeIndex, percent: a.percent })) },
    });
    if (r.status >= 300) throw new KalshiAPIError(r.status, r.text);
  }

  async getPositions(ticker?: string): Promise<any[]> {
    const data = await this.getJson('/trade-api/v2/portfolio/positions', {
      params: ticker ? { ticker } : undefined,
    });
    return data.market_positions ?? data.positions ?? [];
  }

  // Pagination is mandatory: the reconciler drops tracked orders it doesn't see, so a missing page orphans live ones.
  async getOpenOrders(ticker: string, status = 'resting', pageLimit = 200): Promise<any[]> {
    return this.pagedOrders({ ticker, status, limit: pageLimit }, pageLimit);
  }

  async getAllOpenOrders(status = 'resting', pageLimit = 200): Promise<any[]> {
    return this.pagedOrders({ status, limit: pageLimit }, pageLimit);
  }

  private async pagedOrders(baseParams: Record<string, string | number>, pageLimit: number): Promise<any[]> {
    const out: any[] = [];
    let cursor = '';
    for (;;) {
      const params: Record<string, string | number> = { ...baseParams };
      if (cursor) params.cursor = cursor;
      const data = await this.getJson('/trade-api/v2/portfolio/orders', { params });
      const batch: any[] = data.orders ?? [];
      out.push(...batch);
      cursor = data.cursor ?? '';
      if (!cursor || batch.length < pageLimit) break;
    }
    return out;
  }

  // A 404 here is routine: for a second or so after placement the order isn't indexed for queue position yet.
  async getQueuePosition(orderId: string): Promise<number | null> {
    const data = await this.getJson(
      `/trade-api/v2/portfolio/orders/${encodeURIComponent(orderId)}/queue_position`,
      {
        timeoutMs: 5_000,
        expected: [404],
        expectedNote: 'not indexed yet — normal for a fresh order',
      },
    );
    const raw = data.queue_position_fp;
    return raw === null || raw === undefined ? null : Number(raw);
  }

  // `market_tickers` must be one comma-separated param (repeating it 400s); null response means "none", not "failed".
  async getQueuePositions(tickers: string[]): Promise<{ orderId: string; ahead: number }[]> {
    if (!tickers.length) return [];
    const data = await this.getJson('/trade-api/v2/portfolio/orders/queue_positions', {
      params: { market_tickers: tickers.join(',') },
      timeoutMs: 6_000,
    });
    const rows: any[] = Array.isArray(data?.queue_positions) ? data.queue_positions : [];
    if (rows.length && !this.loggedQueueShape) {
      this.loggedQueueShape = true;
      logOdd('rest', `queue_positions row shape: ${JSON.stringify(Object.keys(rows[0]))}`);
    }
    const out: { orderId: string; ahead: number }[] = [];
    for (const r of rows) {
      const orderId = String(r?.order_id ?? r?.orderId ?? r?.id ?? '');
      const raw = r?.queue_position_fp ?? r?.queue_position ?? r?.position_fp ?? r?.position;
      if (!orderId || raw === null || raw === undefined) continue;
      const ahead = Number(raw);
      if (Number.isFinite(ahead)) out.push({ orderId, ahead });
    }
    return out;
  }

  async getSettlements(pageLimit = 100): Promise<any[]> {
    const out: any[] = [];
    let cursor = '';
    for (;;) {
      const params: Record<string, string | number> = { limit: pageLimit };
      if (cursor) params.cursor = cursor;
      const data = await this.getJson('/trade-api/v2/portfolio/settlements', { params, timeoutMs: 15_000 });
      const batch: any[] = data.settlements ?? [];
      out.push(...batch);
      cursor = data.cursor ?? '';
      if (!cursor || batch.length < pageLimit) break;
    }
    return out;
  }

  // Makes IndexedDB a cache, not a system of record: the ledger rebuilds from here by client_order_id.
  async getFills(opts: { ticker?: string; minTs?: number; pageLimit?: number; maxPages?: number } = {}): Promise<any[]> {
    const pageLimit = opts.pageLimit ?? 200;
    const maxPages = opts.maxPages ?? 50;
    const out: any[] = [];
    let cursor = '';
    for (let page = 0; page < maxPages; page++) {
      const params: Record<string, string | number> = { limit: pageLimit };
      if (opts.ticker) params.ticker = opts.ticker;
      if (opts.minTs) params.min_ts = opts.minTs;
      if (cursor) params.cursor = cursor;
      const data = await this.getJson('/trade-api/v2/portfolio/fills', { params, timeoutMs: 15_000 });
      const batch: any[] = data.fills ?? [];
      out.push(...batch);
      cursor = data.cursor ?? '';
      if (!cursor || batch.length < pageLimit) break;
    }
    return out;
  }

  // Legacy POST /portfolio/orders is retired (410); orders go through Create-Order-V2 with a different shape.
  private static readonly V2_ORDERS_PATH = '/trade-api/v2/portfolio/events/orders';

  private toV2Order(p: PlacePayload): Record<string, unknown> {
    const v2: Record<string, unknown> = {
      ticker: p.ticker,
      side: p.side === 'yes' ? 'bid' : 'ask', // bid = buy YES, ask = sell YES
      count: (p.countMq / 1000).toFixed(2),
      price: mcToDollarString(p.yesPriceMc),
      // post_only rests; a plain limit crosses then rests — both good_till_canceled.
      time_in_force: p.mode === 'fok' ? 'fill_or_kill' : 'good_till_canceled',
      self_trade_prevention_type: p.selfTradePreventionType ?? 'maker',
      client_order_id: p.clientOrderId,
    };
    if (p.mode === 'post') v2.post_only = true;
    return v2;
  }

  private static fromV2Order(v2: any, yesPriceMc: MC | null): NormalizedOrder {
    const rem = countToMq(v2.remaining_count) ?? 0;
    const fill = countToMq(v2.fill_count) ?? 0;
    return {
      order_id: v2.order_id ?? null,
      client_order_id: v2.client_order_id ?? null,
      status: rem > 0 ? 'resting' : fill > 0 ? 'executed' : 'canceled',
      remaining_mq: rem,
      fill_mq: fill,
      yes_price_mc: yesPriceMc,
      raw: v2,
    };
  }

  // Same shape placeOrder returns, so an ambiguity-resolved placement is indistinguishable from a clean ack.
  private static normalizeRestOrder(od: any, note = ''): NormalizedOrder {
    return {
      order_id: od.order_id ?? null,
      client_order_id: od.client_order_id ?? null,
      status: od.status ?? 'resting',
      remaining_mq: countToMq(od.remaining_count_fp ?? od.remaining_count),
      fill_mq: countToMq(od.fill_count_fp ?? od.fill_count),
      yes_price_mc: dollarsToMc(od.yes_price_dollars),
      created_time: od.created_time,
      queue_position: od.queue_position ?? null,
      raw: od,
      resolvedNote: note || 'resolved',
    };
  }

  /** Used to resolve an ambiguous placement by client_order_id. */
  async findOrderByCoid(ticker: string, coid: string, maxPages = 3): Promise<any | null> {
    let cursor = '';
    for (let i = 0; i < maxPages; i++) {
      const params: Record<string, string | number> = { ticker, limit: 200 };
      if (cursor) params.cursor = cursor;
      const data = await this.getJson('/trade-api/v2/portfolio/orders', { params });
      for (const od of data.orders ?? []) {
        if (od.client_order_id === coid) return od;
      }
      cursor = data.cursor ?? '';
      if (!cursor) break;
    }
    return null;
  }

  // Looks up twice with a settle delay, since the exchange's write can lag its reads.
  private async resolveAmbiguousPlace(ticker: string, coid: string, why: string): Promise<NormalizedOrder | null> {
    if (!coid || !ticker) return null;
    for (const delay of [500, 1500]) {
      await sleep(delay);
      let od: any = null;
      try {
        od = await this.findOrderByCoid(ticker, coid);
      } catch {
        od = null;
      }
      if (od) return KalshiClient.normalizeRestOrder(od, `resolved after ${why}`);
    }
    return null;
  }

  // An ambiguous outcome (transport error or 5xx) is resolved by coid lookup, so "failed" and "resting" stay exclusive.
  async placeOrder(p: PlacePayload): Promise<NormalizedOrder> {
    const v2 = this.toV2Order(p);
    let r: RawResponse;
    try {
      r = await this.request('POST', KalshiClient.V2_ORDERS_PATH, { json: v2, tag: p.tag });
    } catch (e) {
      if (!(e instanceof AmbiguousPlacement)) throw e;
      const od = await this.resolveAmbiguousPlace(p.ticker, p.clientOrderId, `transport error (${e.message})`);
      if (od) return od;
      throw new KalshiAPIError(
        0,
        `place ambiguous (transport error: ${e.message}); order not found by coid — treating as NOT placed`,
      );
    }
    if (r.status >= 500) {
      const od = await this.resolveAmbiguousPlace(p.ticker, p.clientOrderId, `HTTP ${r.status}`);
      if (od) return od;
      throw new KalshiAPIError(r.status, r.text);
    }
    if (r.status === 409) {
      // 409 = duplicate client_order_id: the order ALREADY EXISTS. That's a
      // success in disguise — return the live order, don't report a failure.
      const od = await this.resolveAmbiguousPlace(p.ticker, p.clientOrderId, 'HTTP 409 duplicate coid');
      if (od) return od;
      throw new KalshiAPIError(r.status, r.text);
    }
    if (r.status >= 300) throw new KalshiAPIError(r.status, r.text);
    const body = JSON.parse(r.text || '{}');
    return KalshiClient.fromV2Order(body.order ?? body, p.yesPriceMc);
  }

  async getOrder(orderId: string): Promise<any> {
    const data = await this.getJson(`/trade-api/v2/portfolio/orders/${encodeURIComponent(orderId)}`);
    return data.order ?? data;
  }

  // A bare 404 is verified against GetOrder before being surfaced, since callers drop local tracking on a 404.
  async cancelOrder(orderId: string, ticker?: string, tag?: string): Promise<number> {
    // Legacy DELETE /portfolio/orders/{id} is retired (410); cancel now goes through /portfolio/events/orders.
    // `ticker` routes the cancel: without `market_ticker`, a sharded order's cancel 404s on shard 0.
    const r = await this.request('DELETE', `/trade-api/v2/portfolio/events/orders/${encodeURIComponent(orderId)}`,
      { params: ticker ? { market_ticker: ticker } : undefined, tag });
    if (r.status === 410) {
      // Never treat as "already gone": the order is still live on the exchange.
      throw new KalshiAPIError(410, 'cancel endpoint returned 410 Gone — this path has been '
        + 'retired by Kalshi and the order is STILL RESTING. Cancel it from kalshi.com.');
    }
    if (r.status === 404) {
      let od: any;
      try {
        od = await this.getOrder(orderId);
      } catch (e) {
        if (e instanceof KalshiAPIError && e.status === 404) throw new KalshiAPIError(404, 'order not found');
        const st = e instanceof KalshiAPIError ? e.status : 0;
        throw new KalshiAPIError(500, `cancel 404 and verify failed (HTTP ${st}) — treating cancel as NOT effective`);
      }
      const status = String(od.status ?? '').toLowerCase();
      if (['resting', 'open', 'partially_filled', 'partial_fill'].includes(status)) {
        throw new KalshiAPIError(500, 'cancel returned 404 but the order still rests '
          + (ticker ? '(routing issue?)' : '(no ticker to route by — unknown exchange shard)') + ' — cancel NOT effective');
      }
      // The read-back rides along: a caller hedging this order needs its fill count.
      throw new KalshiAPIError(404, `order already ${status || 'closed'}`, od);
    }
    if (r.status >= 300) throw new KalshiAPIError(r.status, r.text);
    return r.status;
  }

  async getOrderbook(ticker: string): Promise<any> {
    const data = await this.getJson(`/trade-api/v2/markets/${encodeURIComponent(ticker)}/orderbook`, { timeoutMs: 5_000 });
    return data.orderbook_fp ?? {};
  }

  async getMarket(ticker: string): Promise<any> {
    const data = await this.getJson(`/trade-api/v2/markets/${encodeURIComponent(ticker)}`);
    return data.market ?? {};
  }

  async getMarkets(params: Record<string, string | number>): Promise<any[]> {
    const data = await this.getJson('/trade-api/v2/markets', { params });
    return data.markets ?? [];
  }

  // Every series in one response — no paging, `limit` ignored, ~15MB parsed. Called once per index rebuild.
  async getSeries(): Promise<any[]> {
    const data = await this.getJson('/trade-api/v2/series', { params: {}, timeoutMs: 45_000 });
    return data.series ?? [];
  }

  // Needs the series ticker, not just the market ticker (no flat endpoint); period_interval is only 1, 60 or 1440.
  async getCandles(
    series: string, ticker: string, startTs: number, endTs: number, periodInterval = 1,
  ): Promise<any[]> {
    const data = await this.getJson(
      `/trade-api/v2/series/${encodeURIComponent(series)}/markets/${encodeURIComponent(ticker)}/candlesticks`,
      { params: { start_ts: startTs, end_ts: endTs, period_interval: periodInterval }, timeoutMs: 15_000 },
    );
    return data.candlesticks ?? [];
  }

  /** One page of public prints, newest first. Caller pages with `cursor`. */
  async getTrades(o: {
    ticker: string; limit?: number; minTs?: number; maxTs?: number; cursor?: string;
  }): Promise<{ trades: any[]; cursor: string }> {
    const params: Record<string, string | number> = { ticker: o.ticker, limit: o.limit ?? 1000 };
    if (o.minTs) params.min_ts = o.minTs;
    if (o.maxTs) params.max_ts = o.maxTs;
    if (o.cursor) params.cursor = o.cursor;
    const data = await this.getJson('/trade-api/v2/markets/trades', { params, timeoutMs: 15_000 });
    return { trades: data.trades ?? [], cursor: data.cursor ?? '' };
  }

  /** One page of events. The caller pages with `cursor`; see core/events.ts. */
  async getEvents(params: Record<string, string | number>): Promise<{ events: any[]; cursor: string }> {
    const data = await this.getJson('/trade-api/v2/events', { params });
    return { events: data.events ?? [], cursor: String(data.cursor ?? '') };
  }
}
